import { sv } from 'date-fns/locale';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { OverflowTip } from 'components';
import { DEFAULT_DATE_FORMAT } from 'constants/dateFormats';
import { useSessionState } from 'hooks';
import { Language, UserExtendedDetails } from 'types';
import { formattedDate } from 'utils';

import { ICONS } from './config';
import { GridW, TileHeader, TileLabel, TileValue, TileW } from './styled';

type ProfileCardMetaProps = Pick<
  UserExtendedDetails,
  | 'countryCode'
  | 'phone'
  | 'email'
  | 'city'
  | 'postcode'
  | 'address1'
  | 'birthDate'
  | 'address2'
  | 'region'
  | 'country'
>;

export function ProfileCardMeta({
  countryCode,
  postcode,
  phone,
  email,
  city,
  address1,
  region,
  country,
  birthDate,
  address2,
}: ProfileCardMetaProps): ReactElement {
  const { t } = useTranslation();
  const [state] = useSessionState('i18nextLng');
  const isSwedish = state === Language.swedish;
  const locales = isSwedish ? sv : undefined;

  const phoneNumber = countryCode ? `+${countryCode} ${phone}` : phone;
  const filteredAddressValues = [address1, address2, city, region, postcode, country].filter(
    Boolean,
  );
  const addressStringValue = filteredAddressValues.join(', ');

  return (
    <GridW>
      {phone && (
        <TileW>
          <TileHeader>
            {ICONS.phone}
            <TileLabel>{t('profile.fields.phone')}</TileLabel>
          </TileHeader>
          <TileValue isLink>
            <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
          </TileValue>
        </TileW>
      )}

      {email && (
        <TileW>
          <TileHeader>
            {ICONS.email}
            <TileLabel>{t('profile.fields.email')}</TileLabel>
          </TileHeader>
          <TileValue isLink onClick={() => window.open(`mailto:${email}`)}>
            <OverflowTip tip={email}>{email}</OverflowTip>
          </TileValue>
        </TileW>
      )}

      {addressStringValue && (
        <TileW>
          <TileHeader>
            {ICONS.address}
            <TileLabel>{t('profile.fields.address')}</TileLabel>
          </TileHeader>
          <TileValue>
            <OverflowTip tip={addressStringValue}>{addressStringValue}</OverflowTip>
          </TileValue>
        </TileW>
      )}

      {birthDate && (
        <TileW>
          <TileHeader>
            {ICONS.date}
            <TileLabel>{t('profile.fields.birthday')}</TileLabel>
          </TileHeader>
          <TileValue>{formattedDate(new Date(birthDate), DEFAULT_DATE_FORMAT, locales)}</TileValue>
        </TileW>
      )}
    </GridW>
  );
}
